"use client"

/**
 * Category picker — same search + tree chrome as Locations, using the
 * centralized exam category library.
 */

import * as React from "react"

import { ExamCategoryTree } from "@/components/exam-categories/exam-category-tree"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { cn } from "@/lib/utils"
import {
  findCategory,
  getCategoryBreadcrumb,
  getChildCategories,
  getSearchExpandedIds,
  type ExamCategory,
  type ExamCategoryScope,
  type ExamCategoryTreeNode,
} from "@/lib/exam-categories"

export function ExamCategorySearchInput({
  value,
  onChange,
  inputRef,
}: {
  value: string
  onChange: (next: string) => void
  inputRef?: React.RefObject<HTMLInputElement | null>
}) {
  return (
    <div className="relative">
      <i
        className="fa-light fa-magnifying-glass pointer-events-none absolute top-1/2 left-2 -translate-y-1/2 text-xs text-muted-foreground"
        aria-hidden="true"
      />
      <input
        ref={inputRef}
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Search categories"
        aria-label="Search categories"
        className={cn(
          "h-7 w-full rounded-md border border-border bg-background ps-7 pe-2 text-base md:text-xs",
          "placeholder:text-placeholder-foreground",
          "focus:border-transparent focus:ring-2 focus:ring-ring focus:outline-none"
        )}
      />
    </div>
  )
}

export interface ExamCategoryPickerPanelProps {
  categories: ExamCategory[]
  tree: ExamCategoryTreeNode[]
  value: string[]
  onChange: (categoryIds: string[]) => void
  multiple?: boolean
  defaultRootOpen?: boolean
  navigationMode?: "tree" | "drill-in"
  autoFocusSearch?: boolean
}

export function ExamCategoryPickerPanel({
  categories,
  tree,
  value,
  onChange,
  multiple = true,
  defaultRootOpen = true,
  navigationMode = "tree",
  autoFocusSearch = true,
}: ExamCategoryPickerPanelProps) {
  const [query, setQuery] = React.useState("")
  const [expandedIds, setExpandedIds] = React.useState<Record<string, boolean>>(
    {}
  )
  const searchInputRef = React.useRef<HTMLInputElement>(null)

  React.useEffect(() => {
    if (autoFocusSearch) searchInputRef.current?.focus()
  }, [autoFocusSearch])

  const mergedExpanded = React.useMemo(() => {
    const searchExpanded = getSearchExpandedIds(categories, query)
    if (searchExpanded.size === 0) return expandedIds
    const next = { ...expandedIds }
    for (const id of searchExpanded) next[id] = true
    return next
  }, [categories, expandedIds, query])

  const toggleOrPick = React.useCallback(
    (categoryId: string) => {
      if (!multiple) {
        onChange([categoryId])
        return
      }
      const selected = new Set(value)
      if (selected.has(categoryId)) selected.delete(categoryId)
      else selected.add(categoryId)
      onChange([...selected])
    },
    [multiple, onChange, value]
  )

  if (navigationMode === "drill-in") {
    return (
      <ExamCategoryDrillInPicker
        categories={categories}
        value={value}
        query={query}
        onQueryChange={setQuery}
        searchInputRef={searchInputRef}
        onSelect={toggleOrPick}
      />
    )
  }

  return (
    <div className="flex h-full max-h-[inherit] min-h-0 flex-col">
      <div className="shrink-0 border-b border-border p-2">
        <ExamCategorySearchInput
          inputRef={searchInputRef}
          value={query}
          onChange={setQuery}
        />
      </div>
      <div
        data-slot="category-picker-panel"
        className="min-h-0 flex-1 overflow-y-auto overscroll-y-contain p-2"
        role="region"
        aria-label="Categories"
      >
        <ExamCategoryTree
          tree={tree}
          categories={categories}
          selectedIds={value}
          showCheckbox={multiple}
          expandedIds={mergedExpanded}
          searchQuery={query}
          defaultRootOpen={defaultRootOpen}
          onToggleExpand={(id, open) =>
            setExpandedIds((current) => ({ ...current, [id]: open }))
          }
          onSelect={toggleOrPick}
        />
      </div>
    </div>
  )
}

const SCOPE_ORDER: ExamCategoryScope[] = [
  "universal",
  "program",
  "course",
  "tag",
]

const SCOPE_LABEL: Record<ExamCategoryScope, string> = {
  universal: "Universal classifications",
  program: "Program categories",
  course: "Course categories",
  tag: "Tags",
}

function categoryRoot(
  categories: ExamCategory[],
  category: ExamCategory
): ExamCategory {
  let current = category
  while (current.parentId) {
    const parent = findCategory(categories, current.parentId)
    if (!parent) break
    current = parent
  }
  return current
}

function inheritedCategoryMeta(
  categories: ExamCategory[],
  category: ExamCategory
) {
  const root = categoryRoot(categories, category)
  return {
    root,
    scope: root.scope ?? ("program" as const),
    treeName: root.treeName ?? root.name,
    permission: root.taggingPermission ?? ("allowed" as const),
    requiresReview: Boolean(root.requiresReview),
  }
}

function directSearchMatches(
  categories: ExamCategory[],
  query: string
): ExamCategory[] {
  const needle = query.trim().toLowerCase()
  if (!needle) return []
  return categories
    .filter(
      (category) =>
        category.isActive &&
        (category.name.toLowerCase().includes(needle) ||
          category.description?.toLowerCase().includes(needle))
    )
    .sort((a, b) =>
      getCategoryBreadcrumb(categories, a.id)
        .map((item) => item.name)
        .join(" ")
        .localeCompare(
          getCategoryBreadcrumb(categories, b.id)
            .map((item) => item.name)
            .join(" ")
        )
    )
}

function CategorySelectControl({
  category,
  selected,
  readOnly,
  onSelect,
}: {
  category: ExamCategory
  selected: boolean
  readOnly: boolean
  onSelect: (id: string) => void
}) {
  if (readOnly) {
    return (
      <span className="inline-flex shrink-0 items-center gap-1 px-1 text-2xs font-medium text-muted-foreground">
        <i className="fa-light fa-lock text-xs" aria-hidden="true" />
        Read only
      </span>
    )
  }

  return (
    <div className="flex size-8 shrink-0 items-center justify-center">
      <Checkbox
        checked={selected}
        onCheckedChange={() => onSelect(category.id)}
        aria-label={`${selected ? "Remove" : "Add"} ${category.name}`}
      />
    </div>
  )
}

function CategoryBrowseRow({
  category,
  categories,
  selected,
  onSelect,
  onOpen,
}: {
  category: ExamCategory
  categories: ExamCategory[]
  selected: boolean
  onSelect: (id: string) => void
  onOpen?: (id: string) => void
}) {
  const children = getChildCategories(categories, category.id)
  const meta = inheritedCategoryMeta(categories, category)
  const readOnly = meta.permission === "read-only"

  return (
    <li
      className={cn(
        "flex min-w-0 items-center rounded-md",
        selected ? "bg-accent text-accent-foreground" : "hover:bg-muted/50"
      )}
    >
      {children.length > 0 ? (
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          className="shrink-0"
          aria-label={`Open ${category.name}`}
          onClick={() => onOpen?.(category.id)}
        >
          <i className="fa-light fa-chevron-right text-xs" aria-hidden="true" />
        </Button>
      ) : (
        <span className="size-8 shrink-0" aria-hidden="true" />
      )}
      <button
        type="button"
        className="flex min-h-10 min-w-0 flex-1 items-center gap-2 py-1.5 text-start focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
        onClick={() =>
          children.length > 0
            ? onOpen?.(category.id)
            : readOnly
              ? undefined
              : onSelect(category.id)
        }
      >
        <i
          className={cn(
            "fa-light shrink-0 text-sm",
            children.length > 0 ? "fa-folder text-brand" : "fa-tag text-muted-foreground"
          )}
          aria-hidden="true"
        />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm">{category.name}</span>
          {category.description ? (
            <span className="block truncate text-xs text-muted-foreground">
              {category.description}
            </span>
          ) : null}
        </span>
        {children.length > 0 ? (
          <span className="shrink-0 text-xs text-muted-foreground tabular-nums">
            {children.length}
          </span>
        ) : null}
        {meta.requiresReview ? (
          <span className="shrink-0 rounded-full bg-warning/10 px-1.5 py-0.5 text-2xs font-medium text-warning-foreground">
            Review
          </span>
        ) : null}
      </button>
      <CategorySelectControl
        category={category}
        selected={selected}
        readOnly={readOnly}
        onSelect={onSelect}
      />
    </li>
  )
}

function CategorySearchResults({
  categories,
  query,
  value,
  onSelect,
}: {
  categories: ExamCategory[]
  query: string
  value: string[]
  onSelect: (id: string) => void
}) {
  const matches = React.useMemo(
    () => directSearchMatches(categories, query),
    [categories, query]
  )
  const selectedIds = React.useMemo(() => new Set(value), [value])
  const grouped = React.useMemo(() => {
    const next = new Map<ExamCategoryScope, ExamCategory[]>()
    for (const category of matches) {
      const scope = inheritedCategoryMeta(categories, category).scope
      next.set(scope, [...(next.get(scope) ?? []), category])
    }
    return next
  }, [categories, matches])

  if (matches.length === 0) {
    return (
      <div className="px-3 py-6 text-center">
        <p className="m-0 text-sm font-medium text-foreground">No categories found</p>
        <p className="mt-1 text-xs text-muted-foreground">
          Try a category, description, standard, or outcome.
        </p>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4 p-2">
      {SCOPE_ORDER.map((scope) => {
        const scoped = grouped.get(scope)
        if (!scoped?.length) return null
        return (
          <section key={scope} aria-labelledby={`category-search-${scope}`}>
            <h4
              id={`category-search-${scope}`}
              className="m-0 px-2 pb-1 text-xs font-medium text-muted-foreground"
            >
              {SCOPE_LABEL[scope]}
            </h4>
            <ul className="m-0 flex list-none flex-col gap-0 p-0">
              {scoped.map((category) => {
                const meta = inheritedCategoryMeta(categories, category)
                const path = getCategoryBreadcrumb(categories, category.id)
                  .map((item) => item.name)
                  .join(" / ")
                return (
                  <li
                    key={category.id}
                    className={cn(
                      "flex min-w-0 items-center rounded-md px-2",
                      selectedIds.has(category.id)
                        ? "bg-accent text-accent-foreground"
                        : "hover:bg-muted/50"
                    )}
                  >
                    <button
                      type="button"
                      className="min-h-12 min-w-0 flex-1 py-1.5 text-start focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
                      onClick={() =>
                        meta.permission === "allowed"
                          ? onSelect(category.id)
                          : undefined
                      }
                    >
                      <span className="block text-sm font-medium">{category.name}</span>
                      <span className="block truncate text-xs text-muted-foreground">
                        {meta.treeName} · {path}
                      </span>
                    </button>
                    <CategorySelectControl
                      category={category}
                      selected={selectedIds.has(category.id)}
                      readOnly={meta.permission === "read-only"}
                      onSelect={onSelect}
                    />
                  </li>
                )
              })}
            </ul>
          </section>
        )
      })}
    </div>
  )
}

function CategoryFolderView({
  folder,
  categories,
  selectedIds,
  onSelect,
  onOpen,
}: {
  folder: ExamCategory
  categories: ExamCategory[]
  selectedIds: Set<string>
  onSelect: (id: string) => void
  onOpen: (id: string | null) => void
}) {
  const rows = getChildCategories(categories, folder.id)
  const trail = getCategoryBreadcrumb(categories, folder.id)
  const parent = folder.parentId
    ? findCategory(categories, folder.parentId)
    : undefined

  return (
    <div className="flex flex-col">
      <div className="sticky top-0 z-10 border-b border-border bg-background px-2 py-2">
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="mb-1 -ms-1 max-w-full justify-start px-2 text-muted-foreground"
          onClick={() => onOpen(folder.parentId)}
        >
          <i className="fa-light fa-arrow-left" aria-hidden="true" />
          <span className="truncate">{parent?.name ?? "All categories"}</span>
        </Button>
        <div className="flex min-w-0 items-center gap-2 px-1">
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-semibold text-foreground">
              {folder.name}
            </span>
            {trail.length > 1 ? (
              <span className="block truncate text-xs text-muted-foreground">
                {trail.slice(0, -1).map((item) => item.name).join(" / ")}
              </span>
            ) : null}
          </span>
          <CategorySelectControl
            category={folder}
            selected={selectedIds.has(folder.id)}
            readOnly={
              inheritedCategoryMeta(categories, folder).permission ===
              "read-only"
            }
            onSelect={onSelect}
          />
        </div>
      </div>
      {rows.length === 0 ? (
        <p className="m-0 px-3 py-5 text-sm text-muted-foreground">
          No subcategories.
        </p>
      ) : (
        <ul className="m-0 flex list-none flex-col gap-0 p-2">
          {rows.map((category) => (
            <CategoryBrowseRow
              key={category.id}
              category={category}
              categories={categories}
              selected={selectedIds.has(category.id)}
              onSelect={onSelect}
              onOpen={onOpen}
            />
          ))}
        </ul>
      )}
    </div>
  )
}

function CategoryScopeView({
  categories,
  roots,
  selectedIds,
  onSelect,
  onOpen,
}: {
  categories: ExamCategory[]
  roots: ExamCategory[]
  selectedIds: Set<string>
  onSelect: (id: string) => void
  onOpen: (id: string) => void
}) {
  const groupedRoots = React.useMemo(() => {
    const next = new Map<ExamCategoryScope, ExamCategory[]>()
    for (const root of roots) {
      const scope = root.scope ?? "program"
      next.set(scope, [...(next.get(scope) ?? []), root])
    }
    return next
  }, [roots])

  return (
    <div className="flex flex-col gap-4 p-2">
      {SCOPE_ORDER.map((scope) => {
        const scoped = groupedRoots.get(scope)
        if (!scoped?.length) return null
        return (
          <section key={scope} aria-labelledby={`category-scope-${scope}`}>
            <h4
              id={`category-scope-${scope}`}
              className="m-0 px-2 pb-1 text-xs font-medium text-muted-foreground"
            >
              {SCOPE_LABEL[scope]}
            </h4>
            <ul className="m-0 flex list-none flex-col gap-0 p-0">
              {scoped.map((category) => (
                <CategoryBrowseRow
                  key={category.id}
                  category={category}
                  categories={categories}
                  selected={selectedIds.has(category.id)}
                  onSelect={onSelect}
                  onOpen={onOpen}
                />
              ))}
            </ul>
          </section>
        )
      })}
    </div>
  )
}

function ExamCategoryDrillInPicker({
  categories,
  value,
  query,
  onQueryChange,
  searchInputRef,
  onSelect,
}: {
  categories: ExamCategory[]
  value: string[]
  query: string
  onQueryChange: (next: string) => void
  searchInputRef: React.RefObject<HTMLInputElement | null>
  onSelect: (id: string) => void
}) {
  const [folderId, setFolderId] = React.useState<string | null>(null)
  const folder = folderId ? findCategory(categories, folderId) : undefined
  const rootCategories = React.useMemo(
    () =>
      categories
        .filter((category) => category.parentId === null && category.isActive)
        .sort(
          (a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name)
        ),
    [categories]
  )
  const selectedIds = React.useMemo(() => new Set(value), [value])
  let content = (
    <CategoryScopeView
      categories={categories}
      roots={rootCategories}
      selectedIds={selectedIds}
      onSelect={onSelect}
      onOpen={setFolderId}
    />
  )
  if (folder) {
    content = (
      <CategoryFolderView
        folder={folder}
        categories={categories}
        selectedIds={selectedIds}
        onSelect={onSelect}
        onOpen={setFolderId}
      />
    )
  }
  if (query.trim()) {
    content = (
      <CategorySearchResults
        categories={categories}
        query={query}
        value={value}
        onSelect={onSelect}
      />
    )
  }

  return (
    <div className="flex h-full max-h-[inherit] min-h-0 flex-col">
      <div className="shrink-0 border-b border-border p-2">
        <ExamCategorySearchInput
          inputRef={searchInputRef}
          value={query}
          onChange={onQueryChange}
        />
      </div>
      <div
        data-slot="category-picker-panel"
        className="min-h-0 flex-1 overflow-y-auto overscroll-y-contain"
        role="region"
        aria-label="Categories"
      >
        {content}
      </div>
    </div>
  )
}
